/**
 * Admin Reviews Tab - Manage customer reviews
 */
import { toast } from "sonner";
import axios from "axios";
import { Star, CheckCircle, X, Trash2 } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { useAdmin } from "./AdminContext";
import { API } from "../../lib/api";

const AdminReviewsTab = () => {
  const { reviews, loadData } = useAdmin();

  const handleApproveReview = async (reviewId) => {
    try {
      await axios.put(`${API}/reviews/${reviewId}/approve`, {}, { withCredentials: true });
      toast.success("Review approved");
      loadData();
    } catch { toast.error("Failed to approve"); }
  };

  const handleDeleteReview = async (reviewId) => {
    if (!window.confirm("Delete this review?")) return;
    try {
      await axios.delete(`${API}/reviews/${reviewId}`, { withCredentials: true });
      toast.success("Review deleted");
      loadData();
    } catch { toast.error("Delete failed"); }
  };

  const pendingReviews = reviews.filter(r => !r.approved && r.status !== "approved");
  const approvedReviews = reviews.filter(r => r.approved || r.status === "approved");

  return (
    <Card>
      <CardHeader>
        <CardTitle><Star className="w-5 h-5 inline mr-2 text-amber-500" />Reviews</CardTitle>
        <CardDescription>Manage customer reviews</CardDescription>
      </CardHeader>
      <CardContent>
        {reviews.length === 0 ? (
          <p className="text-center text-stone-500 py-8">No reviews yet</p>
        ) : (
          <div className="space-y-6">
            {pendingReviews.length > 0 && (
              <div>
                <h3 className="font-semibold text-amber-700 mb-3">Pending Approval ({pendingReviews.length})</h3>
                <div className="space-y-3">
                  {pendingReviews.map(review => (
                    <div key={review.review_id} className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="font-medium">{review.name}</p>
                          <div className="flex gap-1 my-1">
                            {[...Array(5)].map((_, i) => (
                              <Star key={i} className={`w-4 h-4 ${i < review.rating ? 'text-amber-400 fill-amber-400' : 'text-stone-300'}`} />
                            ))}
                          </div>
                          <p className="text-sm text-stone-600">{review.review_text || review.comment}</p>
                        </div>
                        <div className="flex gap-2">
                          <Button size="sm" onClick={() => handleApproveReview(review.review_id)} className="bg-green-600 hover:bg-green-700">
                            <CheckCircle className="w-4 h-4 mr-1" /> Approve
                          </Button>
                          <Button size="sm" variant="destructive" onClick={() => handleDeleteReview(review.review_id)}>
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {approvedReviews.length > 0 && (
              <div>
                <h3 className="font-semibold text-green-700 mb-3">Approved ({approvedReviews.length})</h3>
                <div className="space-y-3">
                  {approvedReviews.map(review => (
                    <div key={review.review_id} className="p-4 bg-white border rounded-lg">
                      <div className="flex justify-between items-start">
                        <div>
                          <div className="flex items-center gap-2">
                            <p className="font-medium">{review.name}</p>
                            <Badge className="bg-green-100 text-green-700">Approved</Badge>
                          </div>
                          <div className="flex gap-1 my-1">
                            {[...Array(5)].map((_, i) => (
                              <Star key={i} className={`w-4 h-4 ${i < review.rating ? 'text-amber-400 fill-amber-400' : 'text-stone-300'}`} />
                            ))}
                          </div>
                          <p className="text-sm text-stone-600">{review.review_text || review.comment}</p>
                        </div>
                        <Button size="sm" variant="ghost" className="text-red-600" onClick={() => handleDeleteReview(review.review_id)}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default AdminReviewsTab;
